// UNIFIED DEVELOPMENTAL WRITING WORKSPACE — a unit's CURRENT meaning ("what I mean now") is its verbatim text plus
// the developments recorded on it; earlier wording is history, not another visible unit.
// AUTHENTIC REPAIR 1 — VISIBLE TEXT == EDITABLE TEXT: the unit's authoritative text is ALWAYS the head (a dialogue
// development never replaces it or strikes it through); live developments are shown as paragraphs after it; the edit
// box loads exactly what is shown (unitEditText) and saving folds the developments into the one text (backend).
const norm = (t) => String(t || "").replace(/\s+/g, " ").trim().toLowerCase();

export function unitView(unit) {
  const devs = (unit?.developments || []).filter((d) => d && (d.text || "").trim());
  // HERMENEUTIC BRIDGE — a development that stands as its OWN unit (unit_id) is shown there, never a second time here
  const live = devs.filter((d) => d.function !== "superseded" && d.resolved !== "revise" && d.resolved !== "superseded" && !d.unit_id);
  const head = (unit?.text || "").trim();
  const headL = norm(head);
  const additions = [];
  for (const d of live) {
    const t = (d.text || "").trim();
    const tl = norm(t);
    if (!t || tl === headL || headL.includes(tl)) continue;
    if (additions.some((a) => norm(a).includes(tl))) continue;           // the same words inside another addition
    const idx = additions.findIndex((a) => tl.includes(norm(a)));
    if (idx >= 0) additions[idx] = t; else additions.push(t);            // a fuller wording replaces the shorter one it contains
  }
  const history = devs
    .filter((d) => d.function === "superseded" || d.resolved === "revise" || d.resolved === "superseded")
    .map((d) => (d.text || "").trim())
    .filter((t, i, arr) => t && !headL.includes(norm(t)) && arr.findIndex((x) => norm(x) === norm(t)) === i);
  return { head, additions, history, developed: additions.length };
}

// What the student edits = what the student sees: the head and every live development, as paragraphs.
export const unitEditText = (unit) => { const v = unitView(unit); return [v.head, ...v.additions].filter(Boolean).join("\n\n"); };

// UDWW 3C-4b — WORKING TITLES ARE FIRST-CLASS once a unit is PARAGRAPH-LIKE: enough words to do a job of its own, or
// ideas gathered under it, or judged paragraph-ready. Tiny developmental units are never asked for a title.
export const PARAGRAPH_MIN_WORDS = 40;
export const wordCount = (t) => String(t || "").trim().split(/\s+/).filter(Boolean).length;
export function paragraphLike(unit, { children = 0, ready = false } = {}) {
  if (!unit) return false;
  const v = unitView(unit);
  return wordCount([v.head, ...v.additions].join(" ")) >= PARAGRAPH_MIN_WORDS || children > 0 || !!ready;
}

// Visual epistemic status: STUDENT-ESTABLISHED (solid) · TWS-PROVISIONAL (tentative) · UNRESOLVED (still developing).
export function unitStatus(unit) {
  if (!unit) return "established";
  if (unit.provenance === "tws_provisional") return "provisional_unit";
  if (unit.placement_provenance === "tws_provisional" || unit.placement_provenance === "tws_inferred") return "provisional_place";
  if (!unit.parent_id && !unit.under_thesis && !(unit.region || "").trim()) return "unresolved";
  return "established";
}

export const isProvisional = (unit) => unitStatus(unit).startsWith("provisional");

// IN-PLACE DIFFERENTIATION — TWS-provisional sub-units that are EXACT spans of the unit's text are rendered inside the
// unit's own text (each span a manipulable part; the remaining words between them), never repeated beneath it.
// Returns ordered, non-overlapping segments [{text}] | [{unit}] or null when nothing segments.
export function segmentUnitText(head, subunits) {
  const text = String(head || "");
  if (!text.trim() || !subunits || !subunits.length) return null;
  const found = [];
  for (const su of subunits) {
    const words = String(su.text || "").trim().split(/\s+/).filter(Boolean);
    if (!words.length) continue;
    const re = new RegExp(words.map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("\\s+"), "i");
    const m = re.exec(text);
    if (!m) continue;
    const start = m.index, end = m.index + m[0].length;
    if (found.some((f) => start < f.end && end > f.start)) continue;   // overlapping spans: the first stays a part
    found.push({ start, end, unit: su });
  }
  if (!found.length) return null;
  found.sort((a, b) => a.start - b.start);
  const out = [];
  let cur = 0;
  for (const f of found) {
    const gap = text.slice(cur, f.start).replace(/^[\s.,;:!?—–-]+|\s+$/g, "");
    if (gap) out.push({ text: gap });
    out.push({ unit: f.unit });
    cur = f.end;
  }
  const tail = text.slice(cur).replace(/^[\s.,;:!?—–-]+|\s+$/g, "");
  if (tail) out.push({ text: tail });
  return out;
}

// ESSAY-AS-WORKSPACE — the thesis is a reading of a LOCUS in the student's writing. The accepted thesis arrives with backend
// offsets (`document.thesis.locus`); a pending candidate is located here the same way the backend locates it: whitespace-,
// case- and quote-insensitive, folded WITHOUT changing length so the offsets hold in the original text.
const QUOTE_FOLD = { "\u201c": '"', "\u201d": '"', "\u201e": '"', "\u2018": "'", "\u2019": "'", "\u2032": "'", "\u2013": "-", "\u2014": "-" };
export const foldQuotes = (s) => String(s || "").replace(/[\u201c\u201d\u201e\u2018\u2019\u2032\u2013\u2014]/g, (c) => QUOTE_FOLD[c]).toLowerCase();
export function findSpan(text, needle) {
  const words = foldQuotes(needle).split(/\s+/).filter(Boolean);
  if (!words.length || !text) return null;
  const re = new RegExp(words.map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("\\s+"));
  const m = re.exec(foldQuotes(text));
  return m ? { start: m.index, end: m.index + m[0].length } : null;
}
// ONE sentence per option when the student marks which sentence carries their main idea.
export const splitSentences = (text) => String(text || "").split(/(?<=[.!?]["”')\]]?)\s+/).map((s) => s.trim()).filter(Boolean);
